import React from 'react';
import { Link } from 'gatsby';
import { useCheckoutUrl, useCartCount } from 'gatsby-theme-shopify-manager';
import { AnimatePresence, motion } from 'framer-motion';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import PropTypes from 'prop-types';

import { useGraphQL } from '../hooks';

function AddToCartAlert({
  product,
  isAlertShown,
  setIsAlertShown,
  variant,
  price,
  image,
}) {
  const MotionDialogOverlay = motion.custom(DialogOverlay);
  const MotionDialogContent = motion.custom(DialogContent);

  const handleClose = () => {
    setIsAlertShown(false);
  };

  const checkoutUrl = useCheckoutUrl();
  const cartCount = useCartCount();

  const { placeholderImage } = useGraphQL();

  return (
    <AnimatePresence>
      <MotionDialogOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        key={isAlertShown}
        isOpen={isAlertShown}
        onDismiss={handleClose}
        className="fixed inset-0 z-40 text-gray-700 bg-black bg-opacity-50"
      >
        <MotionDialogContent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ min: 0, max: 100, bounceDamping: 9 }}
          aria-label="Cart alert"
          className="flex justify-end w-full px-4 py-2 mx-auto my-0 transform bg-transparent pointer-events-none translate-y-14 md:translate-y-14 lg:mt-2 lg:translate-y-24 max-w-7xl sm:px-6 lg:px-8 focus:outline-none"
        >
          <div className="relative top-0 w-full max-w-sm p-4 bg-white shadow pointer-events-auto lg:top-10">
            <div
              aria-hidden
              className="absolute top-0 w-3 h-3 transform rotate-45 -translate-y-1/2 bg-white right-1.5"
            />
            <div>
              <div className="relative flex items-center justify-between">
                <div className="text-lg tracking-wider uppercase">
                  {cartCount} items in your cart
                </div>
                <div className="pl-3 ml-auto">
                  <div className="-mx-1.5 -my-1.5">
                    <button
                      onClick={handleClose}
                      type="button"
                      className="inline-flex rounded-md p-1.5 text-dark-pink focus:outline-none"
                    >
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-start mt-4">
                <div className="relative flex-shrink-0 w-16 h-16 overflow-hidden bg-gray-300">
                  <div className="absolute flex">
                    <img
                      src={image}
                      alt={variant.image?.altText || placeholderImage.publicURL}
                    />
                  </div>
                </div>
                <div className="ml-4">
                  <h2 className="font-medium tracking-wider uppercase text-dark-pink">
                    {product.title}
                  </h2>
                  <div>Size: {variant.size}</div>
                  <div>
                    1&times;{' '}
                    <span className="font-bold">
                      ${Number(price).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid gap-4 mt-6 sm:grid-cols-2">
                <Link
                  to="/cart/"
                  className="text-sm uppercase button button-inverted"
                >
                  View cart
                </Link>
                <a
                  href={checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm uppercase button"
                >
                  Check out
                </a>
              </div>
            </div>
          </div>
        </MotionDialogContent>
      </MotionDialogOverlay>
    </AnimatePresence>
  );
}
AddToCartAlert.propTypes = {
  product: PropTypes.object.isRequired,
  isAlertShown: PropTypes.bool.isRequired,
  setIsAlertShown: PropTypes.func.isRequired,
  variant: PropTypes.object,
  price: PropTypes.string.isRequired,
  image: PropTypes.string,
};

export { AddToCartAlert };
